import {
  BULK_IGNORED_LABEL,
  DISREGARDED_BADGE_LABEL,
  DISREGARDED_OUT_OF_LETTER,
  DISREGARDED_LABEL,
  LETTER_RELEASED_LABEL,
} from "./domain";

const SKU_LIST_SEPARATOR: string = ", ";
const WARNING_LIST_SEPARATOR: string = " — ";

export const SHARED_MESSAGES = {
  CANCEL: "Cancelar",
  BUCKET_CARD_EMPTY_CELL: "Célula vazia",
  DISCOUNT_EDITOR_WITHOUT_DISCOUNT: "Sem desconto aplicado",
  DISCOUNT_EDITOR_SKU_LABEL: (sku: string): string => `Desconto do SKU ${sku}`,
  FINAL_PRICE_EDITOR_SKU_LABEL: (sku: string): string =>
    `Preço final do SKU ${sku}`,
  TABLE_PAGINATION_ROWS_FOUND: (total: number): string =>
    `${total} linha(s) encontrada(s).`,
  TABLE_PAGINATION_ROWS_PER_PAGE: "Linhas por página",
  TABLE_PAGINATION_PAGE_OF: (page: number, totalPages: number): string =>
    `Página ${page} de ${totalPages}`,
  TABLE_PAGINATION_FIRST_PAGE: "Ir para a primeira página",
  TABLE_PAGINATION_PREVIOUS_PAGE: "Ir para a página anterior",
  TABLE_PAGINATION_NEXT_PAGE: "Ir para a próxima página",
  TABLE_PAGINATION_LAST_PAGE: "Ir para a última página",
  EMPTY_VALUE: "--",
  SORTABLE_COLUMN_HEADER_ASC: "Asc",
  SORTABLE_COLUMN_HEADER_DESC: "Desc",
  PAGE_ERROR_FORBIDDEN: "ACESSO NEGADO",
  PAGE_ERROR_INTERNAL: "ERRO INTERNO NO SERVIDOR",
} as const;

export const SELECTION_MESSAGES = {
  CLOSE: "Fechar",
  PROCESSING: "Processando...",
  CYCLE_FALLBACK: "sem ciclo",
  DRILL_FAMILY_REQUIRED: "Família é obrigatória para criar uma nova remarcação.",
  DRILL_LOADING_TITLE: "Price Markdown — Drill",
  DRILL_LOADING_DESCRIPTION: "Materializando remarcação...",
  DRILL_PAGE_TITLE: "Seleção de Produtos",
  DRILL_BACK: "Voltar",
  DRILL_TAB_DETAIL: "Detalhe",
  DRILL_TAB_INDICATORS: "Indicadores",
  DRILL_FAMILY_FALLBACK: "família",
  DRILL_NEW_MARKDOWN_CONTEXT: (family: string, cycle: string): string =>
    `Nova remarcação — ${family} — ${cycle}`,
  DRILL_MARKDOWN_CONTEXT: (family: string, cycle: string): string =>
    `${family} — ${cycle}`,
  DRILL_NEGATIVE_MARGIN_ITEM_ONE: "item",
  DRILL_NEGATIVE_MARGIN_ITEM_MANY: "itens",
  DRILL_NEGATIVE_MARGIN_PREFIX: "Há",
  DRILL_NEGATIVE_MARGIN_SUFFIX: "com margem negativa.",
  DRILL_MARKED_COUNT_SUFFIX: "remarcado(s)",
  COLLECTION_PAGE_TITLE: "Remarcações",
  COLLECTION_CYCLE_PILL: (cycle: string): string => `Ciclo: ${cycle}`,
  COLLECTION_COLUMN_UPDATED_AT: "Última atualização",
  COLLECTION_COLUMN_UPDATED_BY: "Atualizado por",
  COLLECTION_COLUMN_ACTIONS: "Ações",
  COLLECTION_EMPTY: "Nenhuma remarcação encontrada com os filtros aplicados.",
  COLLECTION_OPEN_ACTION: "Abrir remarcação",
  BULK_TITLE: "Ações em massa",
  BULK_EMPTY_SELECTION_TITLE:
    "Selecione ao menos um item para aplicar uma ação em massa",
  BULK_ERROR: "Não foi possível aplicar a ação em massa.",
  BULK_VERB_REMARKED: "remarcados",
  BULK_VERB_UNMARKED: "desmarcados",
  BULK_VERB_SUGGESTED: "sugeridos",
  BULK_VERB_SUGGESTION_REMOVED: "com sugestão removida",
  BULK_IGNORED_SUFFIX: (total: number): string =>
    ` ${total} item(ns) ${BULK_IGNORED_LABEL}.`,
  BULK_DISREGARDED_SUFFIX: (total: number): string =>
    ` ${total} item(ns) ${DISREGARDED_LABEL}.`,
  BULK_SUCCESS: (
    total: number,
    verb: string,
    ignored: string,
    disregarded: string,
  ): string => `${total} item(ns) ${verb}.${ignored}${disregarded}`,
  BULK_OPTION_SUGGEST_LABEL: "Sugerir remarcação",
  BULK_OPTION_SUGGEST_HINT:
    "Marca os itens como sugestão, sem colocá-los na proposta.",
  BULK_OPTION_REMARK_LABEL: "Remarcar valores selecionados",
  BULK_OPTION_REMARK_HINT: "Aplica o desconto e coloca os itens na proposta.",
  BULK_OPTION_CLEAR_LABEL: "Desmarcar",
  BULK_OPTION_CLEAR_HINT:
    "Remove a remarcação desta sessão — os itens voltam ao estado inicial.",
  BULK_OPTION_CLEAR_SUGGESTION_LABEL: "Limpar sugestão",
  BULK_OPTION_CLEAR_SUGGESTION_HINT: "Desfaz as sugestões dos itens.",
  SELECTED_MANY: "itens selecionados",
  SELECTED_ONE: "item selecionado",
  BULK_APPLY: (total: string): string => `Aplicar (${total})`,
  BULK_DISCOUNT_LABEL: "Desconto a aplicar",
  BULK_OVERWRITE_WARNING:
    "O desconto é sobrescrito em todos os itens alcançados, inclusive nos que já foram remarcados nesta sessão.",
  APPROVE_ACTION: "Aprovar",
  APPROVE_BUTTON_TITLE: "Aprovar remarcação submetida",
  APPROVE_DESCRIPTION: (familyLabel: string, cycleLabel: string): string =>
    `Aprovar a remarcação ${familyLabel} - ${cycleLabel}? A remarcação passa de Submetida para Aprovada. Efetivação (geração de carta) é uma ação separada.`,
  APPROVE_FROM_DRAFT_TITLE: "Aprovar direto, sem submeter",
  APPROVE_FROM_DRAFT_DESCRIPTION: (
    familyLabel: string,
    cycleLabel: string,
  ): string =>
    `Aprovar a remarcação ${familyLabel} - ${cycleLabel}? A remarcação passa de Rascunho para Aprovada sem passar pela submissão, e nenhum e-mail é enviado. Efetivação (geração de carta) é uma ação separada.`,
  REAPPROVE_ACTION: "Re-Aprovar",
  REAPPROVE_BUTTON_TITLE: "Re-aprovar remarcação após alterações",
  REAPPROVE_DESCRIPTION: (familyLabel: string, cycleLabel: string): string =>
    `Re-aprovar a remarcação ${familyLabel} - ${cycleLabel} após alterações? Esta ação registra novo evento na auditoria.`,
  APPROVE_SUCCESS_TITLE: "Remarcação aprovada",
  APPROVE_SUCCESS_DESCRIPTION: (
    familyLabel: string,
    cycleLabel: string,
  ): string => `A remarcação ${familyLabel} - ${cycleLabel} foi aprovada.`,
  APPROVE_ERROR: "Não foi possível aprovar a remarcação.",
  APPROVE_BLOCKED_TITLE: "Aprovação bloqueada",
  APPROVE_BLOCKED_DESCRIPTION: (skus: string): string =>
    `Margem negativa nos SKUs ${skus}. Ajuste o desconto antes de aprovar.`,
  REFUSE_ACTION: "Recusar",
  REFUSE_MODAL_TITLE: "Recusar remarcação",
  REFUSE_BUTTON_TITLE: "Recusar remarcação com comentário",
  REFUSE_DESCRIPTION: (familyLabel: string, cycleLabel: string): string =>
    `Recusar a remarcação ${familyLabel} - ${cycleLabel}? Ela volta para o gerente com o seu comentário em destaque e pode ser submetida novamente.`,
  REFUSE_REASON_LABEL: "Motivo da recusa",
  REFUSE_REASON_PLACEHOLDER:
    "Explique o que precisa ser ajustado antes de uma nova submissão.",
  REFUSE_SUCCESS_TITLE: "Remarcação recusada",
  REFUSE_SUCCESS_DESCRIPTION: (
    familyLabel: string,
    cycleLabel: string,
  ): string =>
    `A remarcação ${familyLabel} - ${cycleLabel} voltou para o gerente com o seu comentário.`,
  REFUSE_ERROR: "Não foi possível recusar a remarcação.",
  SUBMIT_ACTION: "Submeter",
  SUBMIT_BUTTON_TITLE: "Envia a remarcação ao CP para aprovação",
  SUBMIT_LOADING_LABEL: "Enviando...",
  SUBMIT_NO_ITEMS_TITLE: "Selecione ao menos 1 item para submeter",
  SUBMIT_NO_PERMISSION_TITLE:
    "Você não tem permissão para submeter remarcações",
  SUBMIT_SUCCESS_TITLE: "Submetido ao CP",
  SUBMIT_SUCCESS_DESCRIPTION: "A remarcação foi enviada ao CP (gatilho 1).",
  SUBMIT_CHANGES_RESENT_TITLE: "Alterações reenviadas",
  SUBMIT_CHANGE_NOTICE_TITLE:
    "Avisa o CP das alterações, sem mudar o estado da remarcação",
  SUBMIT_CHANGE_NOTICE_DESCRIPTION:
    "O CP foi avisado das alterações; a remarcação segue submetida.",
  SUBMIT_RESEND_ACTION: "Reenviar",
  SUBMIT_RESEND_CHANGES_ACTION: "Reenviar alterações",
  SUBMIT_RESUBMIT_BUTTON_TITLE:
    "Reenvia a remarcação ao CP (dispara aviso de alteração)",
  SUBMIT_RESUBMITTED_DESCRIPTION:
    "A remarcação foi resubmetida ao CP (gatilho 2).",
  SUBMIT_WARNINGS_TITLE: "Avisos",
  SUBMIT_ERROR: "Não foi possível submeter a remarcação.",
  VIOLATORS_TITLE: "Submissão bloqueada",
  VIOLATORS_DESCRIPTION:
    "Os SKUs abaixo estão com margem negativa (MC-9). Ajuste o desconto e submeta novamente. Nenhum item foi apagado ou movido.",
  VIOLATORS_GO_TO_ITEM: "Ir para o item",
  OPTION_ALL_FEMININE: "Todas",
  OPTION_ALL_MASCULINE: "Todos",
  COLLECTION_FAMILY_LABEL: "Família",
  COLLECTION_FAMILY_PLACEHOLDER: "Selecionar família",
  COLLECTION_STATUS_LABEL: "Status",
  COLLECTION_STATUS_PLACEHOLDER: "Selecionar status",
  COLLECTION_CYCLE_LABEL: "Ciclo",
  COLLECTION_CYCLE_CURRENT: "Atual",
  COLLECTION_CYCLE_BY_MONTH: "Por mês",
  COLLECTION_CYCLE_BY_PERIOD: "Por período",
  COLLECTION_MONTH_LABEL: "Mês",
  COLLECTION_MONTH_PLACEHOLDER: "Selecionar mês",
  COLLECTION_MONTH_EMPTY_PLACEHOLDER: "Sem ciclos",
  COLLECTION_PERIOD_LABEL: "Período",
  COLLECTION_PERIOD_PLACEHOLDER: "dd/mm/aa a dd/mm/aa",
  DRILL_MATRIX_LABEL: "Matriz",
  DRILL_STATUS_ST_LABEL: "Status ST",
  DRILL_STATUS_AGE_LABEL: "Status Idade",
  DRILL_STATUS_AGE_UP_TO_30: "Até 30 dias",
  DRILL_STATUS_AGE_BETWEEN_31_AND_90: "Entre 31 e 90 dias",
  DRILL_STATUS_AGE_BETWEEN_91_AND_120: "Entre 91 e 120 dias",
  DRILL_STATUS_AGE_OVER_120: "Acima de 120 dias",
  DRILL_ORIGIN_FAMILY_LABEL: "Família de origem",
  DRILL_DEPARTMENT_LABEL: "Departamento",
  DRILL_GROUP_LABEL: "Grupo",
  DRILL_SUBGROUP_LABEL: "Sub-grupo",
  DRILL_CODE_LABEL: "Código (SKU)",
  DRILL_CODE_PLACEHOLDER: "ex.: 480000",
  DRILL_ADVANCED_FILTERS_TRIGGER: "Filtros avançados",
  DRILL_ADVANCED_DESCRIPTION_LABEL: "Descrição",
  DRILL_ADVANCED_AGE_FROM_LABEL: "Idade (de)",
  DRILL_ADVANCED_AGE_TO_LABEL: "Idade (até)",
  DRILL_ADVANCED_ST_LESS_THAN_LABEL: "ST menor que (%)",
  DRILL_ADVANCED_STOCK_FROM_LABEL: "Estoque (de)",
  DRILL_ADVANCED_STOCK_TO_LABEL: "Estoque (até)",
  DRILL_ADVANCED_LAST_MARKDOWN_FROM_LABEL: "Última remarcação (de)",
  DRILL_ADVANCED_LAST_MARKDOWN_TO_LABEL: "Última remarcação (até)",
  MULTI_SELECT_NONE: "Nenhum",
  MULTI_SELECT_SELECTED_SUFFIX: " selecionados",
  DRILL_CHECK_NEGATIVE_MARGIN: "Margem negativa",
  DRILL_CHECK_REMARKED: "Remarcados",
  DRILL_CHECK_SUGGESTED: "Sugeridos",
  DRILL_RESTORE_DEFAULT_FILTERS: "Restaurar filtros ao padrão",
  SELECTION_CLEAR: "Limpar seleção",
  SELECTION_ALL_FILTER_PREFIX: "Todos os",
  SELECTION_ALL_FILTER_SUFFIX: "itens do filtro estão selecionados.",
  SELECTION_SELECT_ALL_FILTER_PREFIX: "Selecionar todos os",
  SELECTION_SELECT_ALL_FILTER_SUFFIX: "do filtro",
  DRILL_SELECT_ALL_LABEL: "Selecionar todos",
  DRILL_SELECT_ROW_LABEL: "Selecionar linha",
  DRILL_COLUMN_SUGGESTION: "Sugestão",
  DRILL_HISTORY_TOOLTIP: "Ver histórico",
  DRILL_HISTORY_ITEM_LABEL: (sku: string): string =>
    `Ver histórico do item ${sku}`,
  DRILL_AGE_DAYS: (days: number): string => `${days}d`,
  DRILL_ATTRIBUTE_SEASONAL: "Sazonal",
  DRILL_ATTRIBUTE_KVI: "KVI",
  DRILL_ATTRIBUTE_PROMOTION: "Em promoção",
  EXPORT_TRIGGER_LABEL: "Exportar planilha Excel",
  EXPORT_LOADING_LABEL: "Exportando...",
  EXPORT_REMARKED_ACTION: "Exportar Remarcados",
  EXPORT_ALL_ACTION: "Exportar tudo",
  EXPORT_NO_ITEMS: "Remarque algum produto para exportar.",
  EXPORT_ERROR: "Não foi possível exportar a Seleção.",
  DAILY_UPDATE_BANNER_TITLE: "Alterada pela atualização diária",
  DAILY_UPDATE_BANNER_DESCRIPTION:
    "Itens desta remarcação foram remarcados e a matriz foi atualizada. Os valores foram recalculados com o mesmo desconto. Revise antes de seguir.",
  REFUSAL_COMMENT_BANNER_TITLE: "Recusada pelo Central Planning",
  OUT_OF_DATE_BANNER_TITLE: "Aprovada antes da matriz atual",
  OUT_OF_DATE_BANNER_DESCRIPTION:
    "Esta remarcação foi aprovada em ciclo anterior ou antes da última atualização da matriz.",
  OUT_OF_DATE_BANNER_ONLY_REMARKED:
    " A lista mostra apenas os itens remarcados, com os valores preservados na aprovação.",
  BUDGET_BAR_PERIOD_DEFAULT: "Verba - ciclo atual",
  BUDGET_BAR_OF_TOTAL: (value: string): string => `de ${value}`,
  BUDGET_BAR_MONTH_AMOUNT: (value: string): string => `${value} do mês`,
  BUDGET_BAR_ACCUMULATED_PLUS: (value: string): string =>
    ` mais ${value} acumulados`,
  BUDGET_BAR_ACCUMULATED_MINUS: (value: string): string =>
    ` menos ${value} de dívida acumulada`,
  BUDGET_BAR_MANUAL_ENTRIES: (value: string): string =>
    `${value} em lançamentos manuais`,
  BUDGET_BAR_OVER_BUDGET: (value: string): string =>
    `${value} acima da verba, ajuste a remarcação`,
  BUDGET_BAR_NO_BUDGET: "Sem verba disponível neste mês",
  BUDGET_BAR_REMAINING: (value: string): string =>
    `${value} restante na verba do mês`,
  BUDGET_BAR_IDEAL_RANGE: " — dentro da faixa ideal",
  BUDGET_BAR_EOL_POCKET: "Bolso Mais de 120 dias (CP)",
  SUMMARY_DIMENSION_FINAL_PRICE: "Preço Final",
  SUMMARY_FAMILY_DEPARTMENT: "Família / Departamento",
  SUMMARY_EMPTY_DRILL: (dimensionLabel: string): string =>
    `Nenhum item marcado ainda. Marque itens na aba Detalhe para ver a visão por ${dimensionLabel.toLowerCase()}.`,
  SUMMARY_KPI_MARKDOWN_TOTAL: "Markdown total",
  SUMMARY_KPI_MARKED_ITEMS: "Itens marcados",
  SUMMARY_TABLE_EMPTY:
    "Nenhum item marcado ainda. Marque itens na aba Detalhe para ver os indicadores.",
  SUMMARY_COLUMN_STOCK_QTY: "Stock Qty",
  SUMMARY_COLUMN_STOCK_RS: "Stock R$",
  SUMMARY_COLUMN_MARKDOWN: "Markdown",
  SUMMARY_COLUMN_STOCK_MARGIN: "Stock Margin",
  SUMMARY_COLUMN_NEW_STOCK_MARGIN: "New Stock Margin",
  SUMMARY_COLUMN_AVG_DISCOUNT: "Avg. Discount %",
  SUMMARY_COLUMN_ST_PERCENT: "ST %",
  SUMMARY_TOTAL: "Total",
  MARK_SUGGEST: "Sugerir",
  MARK_SUGGESTED: "Sugerido",
  MARK_SUGGEST_LABEL: (sku: string): string => `Sugerir remarcação ${sku}`,
  MARK_REMARK: "Remarcar",
  MARK_REMARKED: "Remarcado",
  MARK_REMARK_LABEL: (sku: string): string => `Remarcar ${sku}`,
  ITEM_HISTORY_TITLE: "Histórico do item",
  ITEM_HISTORY_TITLE_SKU: (sku: string): string => `Histórico do item ${sku}`,
  ITEM_HISTORY_LOAD_MORE: "Ver mais",
  ITEM_HISTORY_ERROR: "Não foi possível carregar o histórico deste item.",
  ITEM_HISTORY_EMPTY: "Sem histórico registrado",
  ITEM_HISTORY_SYSTEM_AUTHOR: "sistema",
  ITEM_HISTORY_CYCLE: (cycle: string): string => `ciclo ${cycle}`,
  ITEM_HISTORY_REASON: (reason: string): string => `motivo: ${reason}`,
  NEW_MARKDOWN_ACTION: "Nova remarcação",
  NEW_MARKDOWN_NO_MATRIX: "Sem matriz ingerida — atualize a matriz para remarcar.",
  EDIT_DISCOUNT_SAVE_ERROR: "Não foi possível salvar o desconto.",
  EDIT_FINAL_PRICE_ERROR: "Não foi possível calcular o preço final.",
  EDIT_MARK_SAVE_ERROR: "Não foi possível salvar a marcação.",
  INVALID_TRANSITION_TITLE: "Transição inválida",
  INVALID_TRANSITION_DESCRIPTION: (from: string, to: string): string =>
    `Não é possível mover de ${from} para ${to}.`,
  INVALID_TRANSITION_UNKNOWN_STATE: "?",
  NO_PERMISSION_TITLE: "Sem permissão",
  NO_WRITE_PERMISSION:
    "Você não tem permissão de escrita na Remarcação de Preço.",
} as const;

export const BUDGET_MESSAGES = {
  MONTH_BUDGET: "Verba do mês",
  MONTH_LABEL: "Mês",
  CURRENT_MONTH: "Mês corrente",
  SYNC_STARTED_TITLE: "Sincronização iniciada",
  SYNC_STARTED_DESCRIPTION:
    "A verba do mês está sendo lida do DW. A tela recarrega sozinha ao terminar.",
  SYNC_DONE_TITLE: "Verba sincronizada",
  SYNC_DONE_DESCRIPTION: "A verba do mês foi atualizada a partir do DW.",
  SYNC_WAIT_TITLE: "Aguarde para atualizar de novo",
  SYNC_ERROR_TITLE: "Erro ao sincronizar a verba",
  SYNC_ERROR_DESCRIPTION:
    "Não foi possível iniciar a sincronização da verba.",
  REFRESH_ACTION: "Atualizar verba",
  REFRESH_LOADING: "Sincronizando...",
  MANUAL_INPUT_PLACEHOLDER: "R$ 0,00",
  MANUAL_INPUT_LABEL: (familyLabel: string): string =>
    `Verba do mês — ${familyLabel}`,
  MANUAL_SAVED_TITLE: "Verba do mês salva",
  MANUAL_SAVED_DESCRIPTION: (value: string, familyLabel: string): string =>
    `${value} definidos para ${familyLabel} neste mês.`,
  MANUAL_SAVE_ERROR_TITLE: "Erro ao salvar a verba do mês",
  MANUAL_CONFLICT_TITLE: "Verba do mês não gravada",
  MANUAL_SAVE_ERROR: "Não foi possível salvar a verba do mês.",
  COLUMN_FAMILY_TITLE: "Família",
  COLUMN_FAMILY_HELP: "Família de produtos a que a verba pertence.",
  COLUMN_ORIGIN_TITLE: "Origem",
  COLUMN_ORIGIN_HELP:
    "De onde veio o valor do mês: DW quando foi sincronizado (já com a cota cedida ao pool); Digitado quando o DW não trouxe a família e vale o valor digitado pelo administrador; Cota das famílias no pool Mais de 120 dias; Sem verba definida quando não há nenhum dos dois.",
  COLUMN_DW_TITLE: "Verba do DW",
  COLUMN_DW_HELP:
    "Valor sincronizado com o DW para a família neste mês, antes do rateio. Vazio quando o DW não trouxe a família.",
  COLUMN_MONTH_HELP:
    "Verba que a família usa neste mês. Com DW, cada família cede 1/(n+1) do próprio valor para o pool Mais de 120 dias (n = famílias com linha do DW no mês), e o total do DW se preserva. Sem DW, vale o valor digitado pelo administrador no mês corrente.",
  COLUMN_MARKDOWNS_TITLE: "Remarcações",
  COLUMN_MARKDOWNS_HELP:
    "Quanto as remarcações já consomem: as efetivadas no mês (carta emitida) mais as abertas, como estimativa.",
  COLUMN_ENTRIES_TITLE: "Lançamentos",
  COLUMN_ENTRIES_HELP:
    "Débitos registrados à mão nesta tela, que reduzem a verba sem passar por remarcação.",
  COLUMN_CURRENT_TITLE: "Verba atual",
  COLUMN_CURRENT_HELP:
    "O que ainda resta: verba do mês menos remarcações menos lançamentos. Negativo significa estouro.",
  SYNC_UNKNOWN_ERROR: "erro desconhecido",
  SYNC_FAILED_STATUS: (date: string, error: string): string =>
    `Última sincronização falhou em ${date}: ${error}`,
  SYNC_OK_STATUS: (date: string): string => `Sincronizado com o DW em ${date}`,
  SYNC_NEVER_STATUS:
    "Ainda sem sincronização do DW para este mês. Famílias sem linha do DW usam o valor digitado ou ficam sem verba definida.",
  PAGE_FOOTNOTE:
    "A verba do mês vem do DW: cada família cede 1/(n+1) do próprio valor para o pool Mais de 120 dias, e o total do DW se preserva. Quando o DW não traz a família, vale o valor digitado pelo administrador no mês corrente. A verba atual é a verba do mês menos as remarcações e os lançamentos manuais.",
  ENTRIES_VIEW_ACTION: "Ver lançamentos",
  ENTRY_ACTION: "Lançar",
  MANUAL_ENTRIES_TITLE: (familyLabel: string): string =>
    `Lançamentos manuais — ${familyLabel}`,
  ENTRY_VALUE_LABEL: "Valor",
  ENTRY_VALUE_INPUT_LABEL: (familyLabel: string): string =>
    `Valor do lançamento — ${familyLabel}`,
  ENTRY_DESCRIPTION_LABEL: "Descrição",
  ENTRY_DESCRIPTION_INPUT_LABEL: (familyLabel: string): string =>
    `Descrição do lançamento — ${familyLabel}`,
  ENTRIES_COLUMN_DATE: "Data",
  ENTRIES_COLUMN_AUTHOR: "Autor",
  ENTRIES_LOADING: "Carregando...",
  ENTRIES_EMPTY: "Nenhum lançamento manual neste mês.",
  ENTRY_REVERSE_LABEL: (value: string): string =>
    `Estornar lançamento de ${value}`,
  ENTRY_CREATED_TITLE: "Lançamento registrado",
  ENTRY_CREATED_DESCRIPTION: (value: string, familyLabel: string): string =>
    `${value} debitados da verba de ${familyLabel}.`,
  ENTRY_CREATE_ERROR_TITLE: "Erro ao lançar",
  ENTRY_CREATE_ERROR: "Não foi possível registrar o lançamento.",
  ENTRY_REVERSED_TITLE: "Lançamento estornado",
  ENTRY_REVERSED_DESCRIPTION: (value: string, familyLabel: string): string =>
    `${value} devolvidos à verba de ${familyLabel}.`,
  ENTRY_REVERSE_ERROR_TITLE: "Erro ao estornar",
  ENTRY_CONFLICT_TITLE: "Lançamento alterado por outro usuário",
  ENTRY_REVERSE_ERROR: "Não foi possível estornar o lançamento.",
  ENTRIES_FOOTNOTE:
    "Lançamentos manuais debitam a verba atual da família sem alterar o valor sincronizado do DW nem as remarcações. Estornar devolve o valor à verba.",
} as const;

export const OVERVIEW_MESSAGES = {
  PAGE_TITLE: "Visão Geral",
  TAB_MATRIX: "Matriz",
  TAB_INDICATORS: "Indicadores Gerais",
  TAB_BUDGET: "Verba Geral",
  CYCLE_PILL_EMPTY: "Ciclo",
  CYCLE_PILL: (cycle: string): string => `Ciclo ${cycle}`,
  MONTH_PILL: (month: string): string => `Mês ${month}`,
  REMARK_ACTION: "Remarcar",
  REMARK_HINT_NO_MATRIX:
    "Sem matriz ingerida — atualize a matriz para remarcar",
  REMARK_HINT_PICK_FAMILY: "Selecione uma família específica para remarcar",
  TOTAL_MARKDOWN: "Total Markdown (R$)",
  TOTAL_QTY: "Total Qtd",
  LAST_INGESTION: (date: string): string =>
    `Última ingestão da matriz DW: ${date}`,
  FILTER_FAMILY_LABEL: "Família",
  FILTER_FAMILY_PLACEHOLDER: "Selecionar família",
  FILTER_ALL_FAMILIES: "Todas",
  FILTER_DEPARTMENT_LABEL: "Departamento",
  FILTER_DEPARTMENT_PLACEHOLDER: "Selecionar departamento",
  FILTER_DEPARTMENT_NONE: "Sem departamentos",
  FILTER_ALL_DEPARTMENTS: "Todos",
  GRID_AGE: "Idade",
  GRID_SELL_THROUGH: "Sell-through",
  GRID_SHARE_LABEL: "Share de estoque",
  GRID_METRIC_ST: "ST",
  GRID_METRIC_STOCK_QTY: "Qtd estoque",
  ST_ABOVE_AVERAGE: "Above-Average",
  ST_AVERAGE: "Average",
  ST_BELOW_AVERAGE: "Below-Average",
  AGE_UP_TO_30: "Até 30 dias",
  AGE_BETWEEN_31_AND_90: "31–90 dias",
  AGE_BETWEEN_91_AND_120: "91–120 dias",
  AGE_OVER_120: "120+ dias",
  MATRIX_REFRESH_ACTION: "Atualizar matriz",
  MATRIX_REFRESH_LOADING: "Atualizando...",
  MATRIX_REFRESH_STARTED_TITLE: "Atualização iniciada",
  MATRIX_REFRESH_STARTED_DESCRIPTION:
    "A atualização leva cerca de um minuto. A tela recarrega sozinha ao terminar.",
  MATRIX_REFRESH_DONE_TITLE: "Matriz atualizada",
  MATRIX_REFRESH_DONE_DESCRIPTION:
    "A matriz foi atualizada e as remarcações abertas foram reprojetadas.",
  MATRIX_REFRESH_WAIT_TITLE: "Aguarde para atualizar de novo",
  MATRIX_REFRESH_ERROR_TITLE: "Erro ao atualizar a matriz",
  MATRIX_REFRESH_ERROR_DESCRIPTION:
    "Não foi possível iniciar a atualização da matriz.",
  GENERAL_BUDGET_PERIOD: (month: string): string => `Verba geral - ${month}`,
  GENERAL_BUDGET_ERROR_TITLE: "Não foi possível carregar a verba geral",
  GENERAL_BUDGET_COLUMN_FAMILY: "Família",
  GENERAL_BUDGET_COLUMN_RELEASED: "Verba liberada",
  GENERAL_BUDGET_COLUMN_COMMITTED: "Comprometido",
  GENERAL_BUDGET_COLUMN_IN_PROGRESS: "Em andamento",
  GENERAL_BUDGET_COLUMN_ENTRIES: "Lançamentos",
  GENERAL_BUDGET_COLUMN_CURRENT: "Verba atual",
} as const;

export const INDICATORS_MESSAGES = {
  LOAD_ERROR_TITLE: "Não foi possível carregar os indicadores",
  LOAD_ERROR_DESCRIPTION:
    "Tente novamente em instantes; se persistir, avise o CP.",
  EMPTY: "Sem dados de indicadores para esta remarcação.",
  GENERAL_LOAD_ERROR_TITLE: "Não foi possível carregar os indicadores gerais",
  GENERAL_EMPTY_CUT: (dimensionLabel: string): string =>
    `Nenhum item remarcado no recorte para a visão por ${dimensionLabel.toLowerCase()}.`,
  GENERAL_CUT_LABEL: "Recorte",
  GENERAL_CUT_BY_CYCLE: "Por ciclo",
  GENERAL_CUT_BY_MONTH: "Por mês",
  GENERAL_CYCLE_LABEL: "Ciclo",
  GENERAL_CURRENT_CYCLE: "Ciclo vigente",
  GENERAL_MONTH_LABEL: "Mês",
  GENERAL_MONTH_PLACEHOLDER: "Selecionar mês",
  GENERAL_NO_ACTIVE_CYCLE: "Sem ciclo ativo",
  GENERAL_MARKDOWN_COUNT: (count: number): string =>
    `${count} remarcação(ões)`,
} as const;

export const LETTERS_MESSAGES = {
  CYCLE_LABEL: "Ciclo",
  CYCLE_PLACEHOLDER: "Selecione o ciclo",
  CYCLE_EMPTY_PLACEHOLDER: "Sem ciclos",
  SELECTED_COUNT: (total: number): string => `${total} selecionada(s)`,
  GENERATE_CONSOLIDATED_ACTION: (total: number): string =>
    `Gerar carta consolidada (${total})`,
  GENERATING_LABEL: "Gerando...",
  CONFIRM_GENERATE_LABEL: "Confirmar e gerar",
  CONFIRM_TITLE: "Confirmar geração da carta",
  CONFIRM_DESCRIPTION: (total: number, cycle: string): string =>
    `Você vai efetivar ${total} remarcação(ões) e gerar 1 arquivo consolidado para o ciclo ${cycle}. Depois de gerada, a carta é imutável.`,
  DISREGARDED_ITEMS: (total: number): string =>
    `${total} item(ns) ${DISREGARDED_OUT_OF_LETTER}.`,
  DISREGARDED_SUFFIX: (total: number): string =>
    ` ${total} item(ns) ${DISREGARDED_OUT_OF_LETTER}.`,
  GENERATED_TITLE: "Carta gerada",
  GENERATED_DESCRIPTION: (total: number, disregarded: string): string =>
    `${total} remarcações efetivadas em 1 arquivo.${disregarded} Baixe na aba Emitidas quando estiver pronta.`,
  GENERATE_ERROR_TITLE: "Erro ao gerar carta",
  GENERATE_ERROR: "Não foi possível gerar a carta. Tente novamente.",
  COMPOSER_SELECT_ALL_LABEL: "Selecionar todas as remarcações selecionáveis",
  COMPOSER_SELECT_ROW_LABEL: (markdownId: string): string =>
    `Selecionar remarcação ${markdownId}`,
  COMPOSER_COLUMN_FAMILY: "Família",
  COMPOSER_COLUMN_AUTHOR: "Autor",
  COMPOSER_COLUMN_APPROVED_AT: "Aprovada em",
  COMPOSER_COLUMN_ITEMS: "Itens",
  COMPOSER_COLUMN_WARNINGS: "Avisos",
  COMPOSER_NEGATIVE_MARGIN_BADGE: (total: number): string =>
    `Margem negativa (${total})`,
  COMPOSER_OUT_OF_DATE_BADGE: "Fora do prazo",
  COMPOSER_DISREGARDED_BADGE: (total: number): string =>
    `${DISREGARDED_BADGE_LABEL} (${total})`,
  COMPOSER_EMPTY: "Nenhuma remarcação aprovada disponível para compor.",
  PAGE_TITLE: "Cartas",
  TAB_GENERATE: "Gerar",
  TAB_ISSUED: "Emitidas",
  ALL_CYCLES: "Todos os ciclos",
  CYCLE_FILTER_PILL: (cycleLabel: string): string => `Ciclo ${cycleLabel}`,
  REFRESH_ACTION: "Atualizar",
  REFRESHING_LABEL: "Atualizando...",
  ISSUED_COLUMN_GENERATED_AT: "Data/Hora",
  ISSUED_COLUMN_AUTHOR: "Autor",
  ISSUED_COLUMN_ITEMS: "Itens",
  ISSUED_COLUMN_MARKDOWNS: "Remarcações",
  ISSUED_COLUMN_FILE: "Arquivo",
  ISSUED_COLUMN_ACTIONS: "Ações",
  ISSUED_EMPTY: "Nenhuma carta gerada para este ciclo.",
  RELEASED_INFO: (
    releasedBy: string | null,
    releasedAt: string,
    reason: string | null,
  ): string =>
    `${LETTER_RELEASED_LABEL} ${releasedBy ?? ""} em ${releasedAt}: ${reason ?? ""}`,
  REDOWNLOAD_ACTION: "Baixar novamente",
  DOWNLOAD_ERROR: "Não foi possível baixar a carta.",
  DOWNLOAD_PENDING:
    "Arquivo ainda está sendo gerado. Tente novamente em instantes.",
  RELEASE_SUCCESS: (total: number): string =>
    `Itens da carta liberados. ${total} item(ns) voltam a poder ser remarcados.`,
  RELEASE_ERROR: "Não foi possível liberar os itens da carta.",
  RELEASE_AWAITING_ITEMS: (total: number): string =>
    `${total} item(ns) aguardando aplicação nesta carta.`,
} as const;

export const LIFECYCLE_MESSAGES = {
  SKU_LIST: (skus: string[]): string => skus.join(SKU_LIST_SEPARATOR),
  BLOCKED_SKUS: (description: string, skus: string[]): string =>
    `${description} ${skus.join(SKU_LIST_SEPARATOR)}`,
  WARNING_LIST: (warnings: string[]): string =>
    warnings.join(WARNING_LIST_SEPARATOR),
  REFUSE_COMMENT_COUNTER: (length: number, maxLength: number): string =>
    `${length}/${maxLength}`,
} as const;

export const ITEM_HISTORY_ACTION_LABEL: Record<string, string> = {
  analyst_updated_item_desconto: "atualizou desconto",
  analyst_marked_remarcar: "marcou como Remarcar",
  analyst_marked_sugestao: "marcou como Sugestão",
  analyst_unmarked_item: "removeu marcação",
  analyst_added_item_to_marcacao: "adicionou item",
  analyst_removed_item_from_marcacao: "removeu item",
  analyst_submitted_remarcacao: "submeteu remarcação",
};
